import React, { useState, useRef, useEffect } from "react";
import { useDeal } from "../lib/store";
import { placementApi, contactApi } from "../lib/api";
import { Card, ResearchModal, useResearch, AddToCampaignModal, useOutreach, ComposeEmailModal, useCompose, SiteLink } from "../components/shared";
import * as m from "../lib/model";
import { toast } from "sonner";
import { Upload, Globe, Trash2, Search, Share2, Mail, UserSearch } from "lucide-react";

const S = (v) => (Array.isArray(v) ? v.join(", ") : v == null ? "" : String(v));

export default function PlacementAgents() {
  const { M, industry } = useDeal();
  const [crm, setCrm] = useState([]);
  const [crmStats, setCrmStats] = useState({ total: 0, byType: {} });
  const [crmQ, setCrmQ] = useState("");
  const [crmType, setCrmType] = useState("All");
  const [crmSel, setCrmSel] = useState({});
  const [findingId, setFindingId] = useState(null);
  const crmFileRef = useRef(null);

  const research = useResearch("placement");
  const outreach = useOutreach();
  const compose = useCompose();

  const loadCrm = () => placementApi.list({ q: crmQ || undefined, type: crmType, limit: 500 }).then(setCrm).catch(() => {});
  useEffect(() => { placementApi.stats().then(setCrmStats).catch(() => {}); }, [crm.length]);
  useEffect(() => { const t = setTimeout(loadCrm, 250); return () => clearTimeout(t); }, [crmQ, crmType]); // eslint-disable-line

  const delCrm = async (id) => { await placementApi.remove(id); loadCrm(); };

  const findContact = async (l) => {
    setFindingId(l.id);
    const tid = toast.loading(`Finding a contact at ${l.company || l.name}…`);
    try {
      const r = await contactApi.find({ company: l.company || l.name, website: l.domain, role_hint: "capital raising / investor relations / managing partner or principal" });
      if (!r.found) { toast.error("No public contact found — try the firm's website", { id: tid }); return; }
      await placementApi.update(l.id, { contact: r.name || l.contact, title: r.title || l.title, email: r.email || l.email, phone: r.phone || l.phone, notes: [l.notes, r.note, r.linkedin].filter(Boolean).join(" · ").slice(0, 480) });
      toast.success(`Found ${r.name || "a contact"}${r.title ? `, ${r.title}` : ""}${r.email ? ` · ${r.email}` : ""}`, { id: tid });
      loadCrm();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Contact lookup failed", { id: tid });
    } finally { setFindingId(null); }
  };
  const crmSelCount = Object.values(crmSel).filter(Boolean).length;
  const toggleCrm = (id) => setCrmSel((s) => ({ ...s, [id]: !s[id] }));
  const toggleAllCrm = () => { const all = crm.length && crm.every((l) => crmSel[l.id]); const n = {}; if (!all) crm.forEach((l) => { n[l.id] = true; }); setCrmSel(n); };

  const saveAgent = async (e) => {
    await placementApi.create({
      name: S(e.name), company: S(e.name), type: S(e.type) || "Placement Agent", email: S(e.email),
      domain: S(e.website), focus: S(e.focus), dealSize: S(e.dealSize), tags: S(e.geography),
    });
    toast.success(`Saved ${e.name} to Placement Agent CRM`);
    loadCrm();
  };

  const describeAgent = (e) => (
    <div style={{ fontSize: 12 }}>
      <div><b>{e.name}</b>{e.type ? <span className="tag" style={{ marginLeft: 6, fontSize: 9 }}>{e.type}</span> : null}</div>
      {e.focus && <div style={{ color: "var(--text2)", marginTop: 2 }}>{e.focus}</div>}
      <div style={{ color: "var(--text3)", marginTop: 2, fontSize: 11 }}>{[e.dealSize && `Raises ${e.dealSize}`, e.geography].filter(Boolean).join(" · ")}</div>
      {e.website && <a href={/^https?:/.test(e.website) ? e.website : `https://${e.website}`} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "var(--lime)" }}>{e.website}</a>}
    </div>
  );

  const startResearch = () => research.start(`capital placement agents and advisory firms raising equity and debt for a ${m.fmt$(M.askPrice)} ${(industry || "small business").toLowerCase()} acquisition`, "United States");

  const defaultBody = () =>
    `Hi {{firstName}},\n\nXYZ Ventures is pursuing an acquisition of an established ${(industry || "operating").toLowerCase()} business and is exploring a capital raise.\n\n` +
    `• Purchase price: ${m.fmt$(M.askPrice)}\n• Adjusted EBITDA/SDE: ${m.fmt$(M.sde)}\n• Equity sought: ${m.fmt$(m.exitReturns(M).equityInvested)}\n\n` +
    `We'd welcome a conversation about your placement capabilities and typical mandates. Full materials available under NDA.\n\nBest regards,\nXYZ Ventures`;

  const openCompose = () => {
    const picked = crm.filter((l) => crmSel[l.id]);
    if (!picked.length) return toast.error("Select at least one placement agent");
    compose.start({
      title: "Email Placement Agents + Teaser",
      recipients: picked.map((l) => ({ name: l.name, email: l.email })),
      subject: `Capital raise mandate — ${M.dealName}`,
      body: defaultBody(),
      kind: "placement",
      dealName: M.dealName,
      M,
      vars: [["Deal name", M.dealName], ["Asking price", m.fmt$(M.askPrice)], ["Adj. SDE", m.fmt$(M.sde)], ["Equity sought", m.fmt$(m.exitReturns(M).equityInvested)]],
      draftEndpoint: "/ai/outreach",
      draftPayload: { deal: M, investor: { name: picked[0]?.name || "Placement Agent", firm: picked[0]?.name || "", type: "Placement Agent", contact: picked.length === 1 ? (picked[0]?.contact || "") : "{{firstName}}" } },
    });
  };

  const addSelectedToCampaign = () => {
    const list = crm.filter((l) => crmSel[l.id]).map((l) => ({ name: l.contact || "", email: l.email, company: l.name || l.company, title: l.title, phone: l.phone, website: l.domain, notes: [l.focus, l.dealSize, l.tags].filter(Boolean).join(" · ") }));
    if (!list.length) return toast.error("Select at least one placement agent");
    outreach.start(list, "XYZ Placement Agents");
  };

  const onCrmFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      const delim = lines[0].includes(";") ? ";" : ",";
      const hdr = lines[0].toLowerCase().split(delim).map((h) => h.trim());
      const idx = (k) => hdr.findIndex((h) => h.includes(k));
      const iFn = idx("firstname"), iLn = idx("lastname"), iName = idx("name"), iEmail = idx("email"), iTitle = idx("title") >= 0 ? idx("title") : idx("job"), iCo = idx("company") >= 0 ? idx("company") : idx("firm"), iDom = idx("domain") >= 0 ? idx("domain") : idx("website"), iPh = idx("phone"), iFocus = idx("focus"), iSize = idx("size");
      const items = lines.slice(1).map((l) => {
        const c = l.split(delim).map((x) => x.trim().replace(/^"|"$/g, ""));
        const nm = iFn >= 0 ? `${c[iFn] || ""} ${iLn >= 0 ? c[iLn] || "" : ""}`.trim() : (iName >= 0 ? c[iName] : c[0]);
        return { name: nm, email: iEmail >= 0 ? (c[iEmail] || "").split(",")[0] : "", title: iTitle >= 0 ? c[iTitle] : "", company: iCo >= 0 ? c[iCo] : "", domain: iDom >= 0 ? c[iDom] : "", phone: iPh >= 0 ? (c[iPh] || "").split(",")[0] : "", focus: iFocus >= 0 ? c[iFocus] : "", dealSize: iSize >= 0 ? c[iSize] : "", type: "Placement Agent" };
      }).filter((x) => x.name);
      const res = await placementApi.bulk(items);
      toast.success(`Imported ${res.added} placement agent(s)`);
      loadCrm();
    } catch { toast.error("Could not parse CSV"); }
    finally { if (crmFileRef.current) crmFileRef.current.value = ""; }
  };

  return (
    <div className="page" data-testid="page-placement">
      <div className="page-head">
        <div className="page-title">Placement Agents</div>
        <div className="page-sub">Find capital placement agents &amp; advisory firms to run your equity / debt raise — AI-matched, then email the teaser</div>
      </div>

      <Card title="AI Placement Agent Finder" testid="placement-finder"
        right={<button className="btn btn-primary" onClick={startResearch} data-testid="placement-find-ai"><Globe size={13} style={{ marginRight: 4 }} />Find matching placement agents (AI)</button>}>
        <div className="bullet">Search the live web for real capital placement agents, private-placement / capital advisory firms and boutique investment banks that raise equity and debt for lower-middle-market acquisitions and independent sponsors. Each result is cited — click <b>Save</b> to add it to your CRM below.</div>
      </Card>

      <Card title={`Placement Agent CRM — ${crmStats.total.toLocaleString()} ${crmStats.total === 1 ? "firm" : "firms"}`} className="table-scroll" style={{ marginTop: 12 }} testid="placement-crm"
        right={<div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <Search size={13} style={{ position: "absolute", left: 8, top: 9, color: "var(--text3)" }} />
            <input className="inp" style={{ paddingLeft: 26, width: 190 }} placeholder="Search name, firm, email…" value={crmQ} onChange={(e) => setCrmQ(e.target.value)} data-testid="placement-crm-search" />
          </div>
          <select className="inp" style={{ width: 150 }} value={crmType} onChange={(e) => setCrmType(e.target.value)} data-testid="placement-crm-type">
            <option>All</option>{Object.keys(crmStats.byType || {}).map((t) => <option key={t}>{t}</option>)}
          </select>
          <button className={`btn ${crmSelCount ? "btn-primary" : ""}`} onClick={openCompose} disabled={crmSelCount === 0} data-testid="placement-crm-send"><Mail size={13} style={{ marginRight: 4 }} />Email + Teaser ({crmSelCount})</button>
          <button className="btn" onClick={addSelectedToCampaign} disabled={crmSelCount === 0} data-testid="placement-crm-campaign"><Share2 size={13} />Add to campaign ({crmSelCount})</button>
          <button className="btn" onClick={startResearch} data-testid="placement-crm-ai"><Globe size={13} />Find (AI)</button>
          <button className="btn" onClick={() => crmFileRef.current?.click()} data-testid="placement-crm-upload"><Upload size={13} />Upload CSV</button>
          <input ref={crmFileRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={onCrmFile} data-testid="placement-crm-file" />
        </div>}>
        <table className="tbl" style={{ minWidth: 980 }} data-testid="placement-crm-table">
          <thead><tr><th style={{ width: 28 }}><input type="checkbox" checked={crm.length > 0 && crm.every((l) => crmSel[l.id])} onChange={toggleAllCrm} data-testid="placement-crm-all" /></th><th>Firm</th><th>Contact</th><th>Email</th><th>Focus</th><th>Deal size</th><th>Type</th><th></th></tr></thead>
          <tbody>
            {crm.length === 0 ? <tr><td colSpan={8} style={{ color: "var(--text3)", padding: 12 }}>No placement agents yet. Use “Find matching placement agents (AI)” above or upload a CSV.</td></tr> :
              crm.map((l) => (
                <tr key={l.id} data-testid={`placement-crm-row-${l.id}`}>
                  <td><input type="checkbox" checked={!!crmSel[l.id]} onChange={() => toggleCrm(l.id)} data-testid={`placement-crm-check-${l.id}`} /></td>
                  <td>{l.name}<SiteLink url={l.domain} name={l.name} testid={`placement-site-${l.id}`} />{l.domain ? <div style={{ color: "var(--text3)", fontSize: 10.5 }}>{l.domain}</div> : null}</td>
                  <td style={{ fontSize: 11 }}>{l.contact ? <span>{l.contact}{l.title ? <div style={{ color: "var(--text3)" }}>{l.title}</div> : null}</span> : <span style={{ color: "var(--text3)" }}>—</span>}</td>
                  <td style={{ fontSize: 11 }}>{l.email || <span style={{ color: "var(--text3)" }}>—</span>}</td>
                  <td style={{ fontSize: 11, color: "var(--text2)" }}>{l.focus}</td>
                  <td style={{ fontSize: 11 }}>{l.dealSize}</td>
                  <td><span className="tag" style={{ fontSize: 9 }}>{l.type}</span></td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn" style={{ padding: "3px 7px", marginRight: 4 }} onClick={() => findContact(l)} disabled={findingId === l.id} title="Find a contact via the firm's website & team pages" data-testid={`placement-crm-find-${l.id}`}><UserSearch size={12} style={{ marginRight: 3 }} />{findingId === l.id ? "…" : "Find contact"}</button>
                    <button className="btn" style={{ padding: "3px 6px" }} onClick={() => delCrm(l.id)} data-testid={`placement-crm-del-${l.id}`}><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {crm.length >= 500 && <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 8 }}>Showing first 500 — refine with search or type filter.</div>}
      </Card>

      <ResearchModal open={research.open} onClose={() => research.setOpen(false)} title="Find placement agents — live web search"
        subtitle="Perplexity searches for real capital placement agents, advisory firms and investment banks that raise equity & debt for SMB acquisitions. Save adds them to your CRM with a source."
        kind="placement" query={research.query} setQuery={research.setQuery} location={research.location} setLocation={research.setLocation}
        run={research.run} findMore={research.findMore} loading={research.loading} result={research.result} describe={describeAgent} onSave={saveAgent} />
      <AddToCampaignModal open={outreach.open} onClose={() => outreach.setOpen(false)} contacts={outreach.contacts} defaultGroupName={outreach.defaultName} source="placement" />
      <ComposeEmailModal open={compose.open} onClose={() => compose.setOpen(false)} config={compose.config} />
    </div>
  );
}
